// frontend/src/components/ProviderServices/ServiceCard.tsx
// Mobile-first design with improved layout

import React from 'react';
import { 
  Edit3, 
  Trash2, 
  ToggleLeft, 
  ToggleRight,
  Clock,
  DollarSign,
  Star,
  StarOff,
  Tag,
  MoreVertical
} from 'lucide-react';
import { ProviderService } from '@/lib/api/providerServicesService';

interface ServiceCardProps {
  service: ProviderService;
  onEdit: (service: ProviderService) => void;
  onDelete: (service: ProviderService) => void;
  onToggleStatus: (serviceId: number) => void;
  onToggleFeatured?: (serviceId: number) => void;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ 
  service, 
  onEdit, 
  onDelete, 
  onToggleStatus,
  onToggleFeatured
}) => {
  const [showActions, setShowActions] = React.useState(false);

  return (
    <div className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
      <div className="space-y-4">
        {/* Header Row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Service Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 truncate text-base sm:text-lg">
                  {service.service_name}
                </h3>
                {service.is_featured && (
                  <Star className="h-4 w-4 text-amber-500 fill-current flex-shrink-0" />
                )}
              </div>
              
              {/* Category Badge */}
              {service.category_name && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    <Tag size={10} />
                    {service.category_name}
                  </span>
                  {service.service_type_name && (
                    <span className="text-xs text-gray-500 truncate">
                      {service.service_type_name}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Actions Menu */}
          <div className="flex items-center gap-1">
            {/* Quick Status Toggle */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleStatus(service.provider_service_id);
              }}
              className={`p-2 rounded-lg transition-colors ${
                service.is_active 
                  ? 'text-green-600 hover:bg-green-50' 
                  : 'text-gray-400 hover:bg-gray-50'
              }`}
              title={service.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
            >
              {service.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
            </button>

            {/* Actions Menu */}
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <MoreVertical size={16} />
              </button>

              {showActions && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowActions(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                    <div className="py-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(service);
                          setShowActions(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Edit3 size={14} />
                        Edit Service
                      </button>
                      
                      {onToggleFeatured && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleFeatured(service.provider_service_id);
                            setShowActions(false);
                          }}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          {service.is_featured ? <StarOff size={14} /> : <Star size={14} />}
                          {service.is_featured ? 'Remove Featured' : 'Make Featured'}
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(service);
                          setShowActions(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                        Delete Service
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {service.description && (
          <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
            {service.description}
          </p>
        )}

        {/* Service Details - Mobile Optimized */}
        <div className="space-y-3">
          {/* Price and Duration */}
          {(service.price_range || service.estimated_duration) && (
            <div className="grid grid-cols-1 gap-2">
              {service.price_range && (
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">
                    <span className="font-medium">Price:</span> {service.price_range}
                  </span>
                </div>
              )}

              {service.estimated_duration && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-gray-700">
                    <span className="font-medium">Duration:</span> {service.estimated_duration}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Status Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              service.is_active 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {service.is_active ? 'Active' : 'Inactive'}
            </span>
            
            {service.is_featured && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Featured
              </span>
            )}
            
            {/* Provider Info (for admin view) */}
            {service.provider_info && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {service.provider_info.business_name}
              </span>
            )}
          </div>

          {/* Timestamps - Mobile Optimized */}
          <div className="flex flex-col gap-1 text-xs text-gray-500 sm:flex-row sm:gap-4">
            <span>Created: {new Date(service.created_at).toLocaleDateString()}</span>
            {service.updated_at && service.updated_at !== service.created_at && (
              <span>Updated: {new Date(service.updated_at).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;